import {
  findAvailableTables,
  type RestaurantDemoCustomer,
  type RestaurantDemoOrder,
  type RestaurantDemoReservation,
  type RestaurantDemoState,
} from "./restaurant-sandbox";
import {
  applyInventoryForConfiguredLines,
  modifierGroupsForProduct,
  priceConfiguredLine,
  type RestaurantDemoConfiguredLine,
  type RestaurantDemoV5State,
} from "./restaurant-demo-v5-state";

export type RestaurantCustomerFulfillment = "pickup" | "delivery";

export interface RestaurantCustomerCartLine extends RestaurantDemoConfiguredLine {
  readonly key: string;
}

export interface RestaurantCustomerLineBuildResult {
  readonly ok: boolean;
  readonly line?: RestaurantCustomerCartLine;
  readonly errorCode?: string;
  readonly detail?: string;
}

export interface RestaurantCustomerCommitResult<T> {
  readonly state: RestaurantDemoState;
  readonly value: T;
  readonly idempotentReplay: boolean;
}

type CustomerOrderRecord = RestaurantDemoOrder & {
  readonly customerIntentId: string;
  readonly customerIntentFingerprint: string;
  readonly fulfillment: RestaurantCustomerFulfillment;
  readonly deliveryAddress: string;
  readonly source: "customer_web";
};

type CustomerReservationRecord = RestaurantDemoReservation & {
  readonly customerIntentId: string;
  readonly customerIntentFingerprint: string;
  readonly source: "customer_web";
};

function normalized(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function safeIntentId(value: string): string {
  const normalizedValue = value.trim().replace(/[^A-Za-z0-9_-]/g, "").slice(0, 72);
  if (!normalizedValue) throw new Error("customer_intent_id_required");
  return normalizedValue;
}

function fingerprint(value: unknown): string {
  return JSON.stringify(value);
}

function customerLineFingerprint(line: RestaurantCustomerCartLine): unknown {
  return {
    productId: line.productId,
    quantity: line.quantity,
    modifierIds: (line.modifiers ?? []).map((modifier) => modifier.optionId).sort(),
  };
}

function existingCustomerOrder(order: RestaurantDemoOrder): CustomerOrderRecord | null {
  const record = order as RestaurantDemoOrder & Partial<CustomerOrderRecord>;
  return record.source === "customer_web" && typeof record.customerIntentId === "string" && typeof record.customerIntentFingerprint === "string"
    ? record as CustomerOrderRecord
    : null;
}

function existingCustomerReservation(reservation: RestaurantDemoReservation): CustomerReservationRecord | null {
  const record = reservation as RestaurantDemoReservation & Partial<CustomerReservationRecord>;
  return record.source === "customer_web" && typeof record.customerIntentId === "string" && typeof record.customerIntentFingerprint === "string"
    ? record as CustomerReservationRecord
    : null;
}

function upsertCustomer(state: RestaurantDemoState, name: string, phone: string, purchaseMinor = 0): RestaurantDemoState {
  const existing = state.customers.find((customer) => normalized(customer.name) === normalized(name));
  const now = new Date().toISOString();
  const purchase = Number.isSafeInteger(purchaseMinor) && purchaseMinor > 0 ? purchaseMinor : 0;
  if (existing) {
    return Object.freeze({
      ...state,
      customers: Object.freeze(state.customers.map((customer) => customer.id === existing.id
        ? Object.freeze({
            ...customer,
            phone: phone || customer.phone,
            visits: customer.visits + (purchase > 0 ? 1 : 0),
            totalSpendMinor: customer.totalSpendMinor + purchase,
            lastInteractionAt: now,
          })
        : customer)),
    });
  }
  const customer: RestaurantDemoCustomer = Object.freeze({
    id: `cus-web-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name,
    phone,
    visits: purchase > 0 ? 1 : 0,
    totalSpendMinor: purchase,
    notes: "Creado desde la experiencia cliente demo.",
    lastInteractionAt: now,
  });
  return Object.freeze({ ...state, customers: Object.freeze([customer, ...state.customers]) });
}

function addEvent(state: RestaurantDemoState, id: string, type: string, label: string): RestaurantDemoState {
  if (state.events.some((event) => event.id === id)) return state;
  return Object.freeze({
    ...state,
    events: Object.freeze([{ id, at: new Date().toISOString(), type, label }, ...state.events].slice(0, 80)),
  });
}

export function buildRestaurantCustomerCartLine(input: {
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly productId: string;
  readonly selections: Readonly<Record<string, readonly string[]>>;
  readonly quantity?: number;
}): RestaurantCustomerLineBuildResult {
  const product = input.state.products.find((candidate) => candidate.id === input.productId && candidate.active);
  if (!product) return Object.freeze({ ok: false, errorCode: "product_not_found", detail: "El producto ya no existe." });
  if (!product.available) return Object.freeze({ ok: false, errorCode: "product_unavailable", detail: `${product.name} no está disponible ahora.` });
  const quantity = Math.max(1, Math.min(50, Math.trunc(input.quantity ?? 1)));
  const groups = modifierGroupsForProduct(input.v5, product.id);
  const modifiers: NonNullable<RestaurantDemoConfiguredLine["modifiers"]>[number][] = [];

  for (const group of groups) {
    const selectedIds = [...new Set(input.selections[group.id] ?? [])];
    if (selectedIds.length < group.minSelections || selectedIds.length > group.maxSelections) {
      return Object.freeze({
        ok: false,
        errorCode: "modifier_selection_invalid",
        detail: `${group.name}: elegí entre ${group.minSelections} y ${group.maxSelections}.`,
      });
    }
    for (const optionId of selectedIds) {
      const option = group.options.find((candidate) => candidate.id === optionId);
      if (!option) return Object.freeze({ ok: false, errorCode: "modifier_option_invalid", detail: `Una opción de ${group.name} ya no está disponible.` });
      modifiers.push(Object.freeze({
        groupId: group.id,
        optionId: option.id,
        label: option.name,
        priceDeltaMinor: option.priceDeltaMinor,
      }));
    }
  }

  const key = [product.id, ...modifiers.map((modifier) => modifier.optionId).sort()].join(":");
  return Object.freeze({
    ok: true,
    line: Object.freeze({
      key,
      productId: product.id,
      name: product.name,
      quantity,
      unitPriceMinor: product.priceMinor,
      ...(modifiers.length ? { modifiers: Object.freeze(modifiers) } : {}),
    }),
  });
}

export function addRestaurantCustomerCartLine(
  cart: readonly RestaurantCustomerCartLine[],
  line: RestaurantCustomerCartLine,
): readonly RestaurantCustomerCartLine[] {
  const existing = cart.find((candidate) => candidate.key === line.key);
  if (!existing) return Object.freeze([...cart, line]);
  return Object.freeze(cart.map((candidate) => candidate.key === line.key
    ? Object.freeze({ ...candidate, quantity: Math.min(50, candidate.quantity + line.quantity) })
    : candidate));
}

export function changeRestaurantCustomerCartQuantity(
  cart: readonly RestaurantCustomerCartLine[],
  key: string,
  delta: number,
): readonly RestaurantCustomerCartLine[] {
  return Object.freeze(cart.flatMap((line) => {
    if (line.key !== key) return [line];
    const quantity = line.quantity + delta;
    return quantity <= 0 ? [] : [Object.freeze({ ...line, quantity: Math.min(50, quantity) })];
  }));
}

export function restaurantCustomerCartTotalMinor(cart: readonly RestaurantCustomerCartLine[]): number {
  return cart.reduce((sum, line) => sum + priceConfiguredLine(line), 0);
}

function revalidateCart(
  state: RestaurantDemoState,
  v5: RestaurantDemoV5State,
  cart: readonly RestaurantCustomerCartLine[],
): readonly RestaurantCustomerCartLine[] {
  if (!cart.length) throw new Error("customer_cart_empty");
  return Object.freeze(cart.map((line) => {
    const selections: Record<string, string[]> = {};
    for (const modifier of line.modifiers ?? []) {
      (selections[modifier.groupId] ??= []).push(modifier.optionId);
    }
    const result = buildRestaurantCustomerCartLine({ state, v5, productId: line.productId, selections, quantity: line.quantity });
    if (!result.ok || !result.line) throw new Error(result.errorCode ?? "customer_cart_invalid");
    return result.line;
  }));
}

export function commitRestaurantCustomerOrder(input: {
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly idempotencyKey: string;
  readonly customerName: string;
  readonly phone?: string;
  readonly fulfillment: RestaurantCustomerFulfillment;
  readonly deliveryAddress?: string;
  readonly cart: readonly RestaurantCustomerCartLine[];
}): RestaurantCustomerCommitResult<CustomerOrderRecord> {
  const intentId = safeIntentId(input.idempotencyKey);
  const customerName = input.customerName.trim();
  const phone = input.phone?.trim() ?? "";
  const deliveryAddress = input.deliveryAddress?.trim() ?? "";
  if (!customerName) throw new Error("customer_name_required");
  if (input.fulfillment === "delivery" && !deliveryAddress) throw new Error("delivery_address_required");

  const lines = revalidateCart(input.state, input.v5, input.cart);
  const semanticFingerprint = fingerprint({
    customerName: normalized(customerName),
    phone,
    fulfillment: input.fulfillment,
    deliveryAddress: normalized(deliveryAddress),
    lines: lines.map(customerLineFingerprint),
  });
  const existing = input.state.orders.map(existingCustomerOrder).find((order) => order?.customerIntentId === intentId) ?? null;
  if (existing) {
    if (existing.customerIntentFingerprint !== semanticFingerprint) throw new Error("customer_order_idempotency_conflict");
    return Object.freeze({ state: input.state, value: existing, idempotentReplay: true });
  }

  const order: CustomerOrderRecord = Object.freeze({
    id: `ord-web-${intentId}`,
    customerName,
    tableId: null,
    channel: "assistant",
    status: "submitted",
    paymentStatus: "pending",
    paymentMethod: null,
    lines: Object.freeze(lines),
    totalMinor: restaurantCustomerCartTotalMinor(lines),
    createdAt: new Date().toISOString(),
    customerIntentId: intentId,
    customerIntentFingerprint: semanticFingerprint,
    fulfillment: input.fulfillment,
    deliveryAddress,
    source: "customer_web",
  });

  let next: RestaurantDemoState = Object.freeze({ ...input.state, orders: Object.freeze([order, ...input.state.orders]) });
  next = applyInventoryForConfiguredLines(next, input.v5, lines, `Pedido cliente ${order.id}`);
  next = upsertCustomer(next, customerName, phone, order.totalMinor);
  next = addEvent(next, `evt-${order.id}`, "order", `Pedido web ${order.id} · ${customerName}`);
  return Object.freeze({ state: next, value: order, idempotentReplay: false });
}

export function commitRestaurantCustomerReservation(input: {
  readonly state: RestaurantDemoState;
  readonly idempotencyKey: string;
  readonly customerName: string;
  readonly phone?: string;
  readonly partySize: number;
  readonly date: string;
  readonly time: string;
  readonly durationMinutes?: number;
  readonly area?: string | null;
}): RestaurantCustomerCommitResult<CustomerReservationRecord> {
  const intentId = safeIntentId(input.idempotencyKey);
  const customerName = input.customerName.trim();
  const phone = input.phone?.trim() ?? "";
  const partySize = Math.trunc(input.partySize);
  const durationMinutes = Math.trunc(input.durationMinutes ?? 90);
  if (!customerName) throw new Error("customer_name_required");
  if (!Number.isSafeInteger(partySize) || partySize < 1 || partySize > 30) throw new Error("party_size_invalid");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date)) throw new Error("reservation_date_invalid");
  if (!/^\d{2}:\d{2}$/.test(input.time)) throw new Error("reservation_time_invalid");

  const semanticFingerprint = fingerprint({
    customerName: normalized(customerName),
    phone,
    partySize,
    date: input.date,
    time: input.time,
    durationMinutes,
    area: input.area ?? null,
  });
  const existing = input.state.reservations.map(existingCustomerReservation).find((reservation) => reservation?.customerIntentId === intentId) ?? null;
  if (existing) {
    if (existing.customerIntentFingerprint !== semanticFingerprint) throw new Error("customer_reservation_idempotency_conflict");
    return Object.freeze({ state: input.state, value: existing, idempotentReplay: true });
  }

  const table = findAvailableTables(input.state, input.date, input.time, durationMinutes, partySize, input.area ?? null)[0];
  if (!table) throw new Error("reservation_unavailable");
  const reservation: CustomerReservationRecord = Object.freeze({
    id: `res-web-${intentId}`,
    customerName,
    phone,
    partySize,
    time: input.time,
    date: input.date,
    startTime: input.time,
    durationMinutes,
    tableId: table.id,
    status: "confirmed",
    notes: "Reserva creada desde experiencia cliente demo.",
    createdAt: new Date().toISOString(),
    customerIntentId: intentId,
    customerIntentFingerprint: semanticFingerprint,
    source: "customer_web",
  });

  let next: RestaurantDemoState = Object.freeze({
    ...input.state,
    reservations: Object.freeze([reservation, ...input.state.reservations]),
    tables: Object.freeze(input.state.tables.map((candidate) => candidate.id === table.id
      ? Object.freeze({ ...candidate, status: "reserved" as const })
      : candidate)),
  });
  next = upsertCustomer(next, customerName, phone);
  next = addEvent(next, `evt-${reservation.id}`, "reservation", `Reserva web ${customerName} · ${input.date} ${input.time} · ${table.label}`);
  return Object.freeze({ state: next, value: reservation, idempotentReplay: false });
}
